import { create } from "zustand";
import {
  BillChargeKind,
  BillStatus,
  ChargeSplitMode,
  dayjs,
} from "@boo/shared";
import type {
  Bill,
  BillInput,
  BillListItem,
  Friend,
  ReceiptDraft,
  SplitGroup,
} from "@boo/shared";
import {
  confirmBillSlip,
  createBill,
  createBillShare,
  deleteBill,
  fetchBill,
  fetchBills,
  markBillPersonPaid,
  finalizeBill,
  parseReceipt,
  relinkBillPerson,
  rejectBillSlip,
  reopenBill,
  resolveBillFlag,
  revokeBillShare,
  saveBill,
  setBillPaymentProfile,
  uploadBillReceipt,
} from "../../../services/billApi";
import { downscaleImage } from "../lib/downscaleImage";
import { applyPercentCharges, explodeUnits } from "../lib/billMath";
import {
  OWNER_NAME,
  type BillEditor,
  type EditorCharge,
  type EditorItem,
  type EditorPerson,
} from "../types/bills.types";

interface NewBillSeed {
  transactionId?: string;
  title?: string;
  date?: string;
}

interface BillStoreState {
  bills: BillListItem[];
  listLoading: boolean;
  editor: BillEditor | null;
  editorLoading: boolean;
  scanning: boolean;
  saving: boolean;
  /** Scanned photo waiting for the bill to exist so it can be stored. */
  pendingReceipt: File | null;

  loadBills: () => Promise<void>;
  openBill: (id: string) => Promise<void>;
  /** Pick up friends' claims/flags made through the share link, unless mid-edit. */
  /** Someone else changed the bill while this editor has unsaved edits. */
  remoteChanged: boolean;
  /** Live-update event: apply the change, or flag it when it would clobber edits. */
  syncRemote: (revision: string) => Promise<void>;
  /** Drop unsaved edits and load the latest server copy. */
  reloadRemote: () => Promise<void>;
  startNew: (seed: NewBillSeed) => void;
  closeEditor: () => void;
  scanReceipt: (file: File) => Promise<void>;
  removeBill: (id: string) => Promise<void>;

  setTitle: (title: string) => void;
  setDate: (date: string) => void;
  addFriend: (friend: Friend) => void;
  addGroup: (group: SplitGroup) => void;
  relinkPerson: (personId: string, friend: Friend) => Promise<void>;
  removePerson: (id: string) => void;
  addItem: () => void;
  updateItem: (key: string, patch: Partial<EditorItem>) => void;
  removeItem: (key: string) => void;
  splitUnits: (key: string) => void;
  toggleClaim: (key: string, personId: string) => void;
  claimAll: (key: string) => void;
  toggleGoDutch: () => void;
  addCharge: (kind: BillChargeKind) => void;
  updateCharge: (key: string, patch: Partial<EditorCharge>) => void;
  removeCharge: (key: string) => void;

  /** Persist edits; resolves to the bill id (created on first save). */
  save: () => Promise<string>;
  uploadPendingReceipt: (billId: string) => Promise<void>;
  finalize: () => Promise<void>;
  reopen: () => Promise<void>;

  setPaymentProfile: (profileId: string | null) => Promise<void>;
  createShare: () => Promise<void>;
  revokeShare: () => Promise<void>;
  resolveFlag: (flagId: string) => Promise<void>;
  confirmSlip: (slipId: string) => Promise<void>;
  rejectSlip: (slipId: string) => Promise<void>;
  markPersonPaid: (personId: string) => Promise<void>;
}

const CHARGE_DEFAULTS: Record<
  BillChargeKind,
  { label: string; splitMode: ChargeSplitMode; percent?: number }
> = {
  [BillChargeKind.Service]: {
    label: "Service charge",
    splitMode: ChargeSplitMode.Proportional,
    percent: 10,
  },
  [BillChargeKind.Vat]: {
    label: "VAT",
    splitMode: ChargeSplitMode.Proportional,
    percent: 7,
  },
  [BillChargeKind.Discount]: {
    label: "Discount",
    splitMode: ChargeSplitMode.Proportional,
  },
  [BillChargeKind.Other]: { label: "Other", splitMode: ChargeSplitMode.Equal },
};

function ownerPerson() {
  return {
    id: crypto.randomUUID(),
    name: OWNER_NAME,
    isOwner: true,
    friendId: null,
  };
}

/** Append Friends not already on the bill. */
function withFriends(
  people: EditorPerson[],
  friends: Friend[],
): EditorPerson[] {
  const onBill = new Set(people.map((p) => p.friendId));
  const added = friends
    .filter((f) => !onBill.has(f.id))
    .map((f) => ({
      id: crypto.randomUUID(),
      name: f.name,
      isOwner: false,
      friendId: f.id,
    }));
  return added.length > 0 ? [...people, ...added] : people;
}

function fromBill(bill: Bill): BillEditor {
  return {
    billId: bill.id,
    transactionId: bill.transactionId,
    status: bill.status,
    transactionAmount: bill.transactionAmount,
    title: bill.title,
    date: dayjs(bill.date).format("YYYY-MM-DD"),
    goDutch: bill.goDutch,
    people: bill.people.map((p) => ({
      id: p.id,
      name: p.name,
      isOwner: p.isOwner,
      friendId: p.friendId,
    })),
    items: bill.items.map((i) => ({
      key: i.id,
      name: i.name,
      amount: i.amount,
      quantity: i.quantity,
      claimedBy: i.claimedBy,
    })),
    charges: bill.charges.map((c) => ({
      key: c.id,
      label: c.label,
      kind: c.kind,
      amount: c.amount,
      splitMode: c.splitMode,
    })),
    paid: Object.fromEntries(bill.people.map((p) => [p.id, p.isPaid])),
    finalAmounts: Object.fromEntries(
      bill.people.map((p) => [p.id, p.finalAmount]),
    ),
    reviewed: Object.fromEntries(bill.people.map((p) => [p.id, p.reviewed])),
    readyToFinalize: bill.readyToFinalize,
    ownerDisplayName: bill.ownerDisplayName,
    paymentProfileId: bill.paymentProfileId,
    hasReceipt: bill.hasReceipt,
    share: bill.share,
    flags: bill.flags,
    slips: bill.slips,
    revision: bill.revision,
    dirty: false,
  };
}

function toInput(e: BillEditor): BillInput {
  return {
    title: e.title.trim() || "Meal",
    date: e.date,
    goDutch: e.goDutch,
    people: e.people,
    items: e.items
      .filter((i) => i.name.trim())
      .map((i) => ({
        name: i.name.trim(),
        amount: i.amount,
        quantity: i.quantity,
        claimedBy: i.claimedBy,
      })),
    charges: e.charges
      .filter((c) => c.amount !== 0)
      .map((c) => ({
        label: c.label.trim() || CHARGE_DEFAULTS[c.kind].label,
        kind: c.kind,
        amount: c.amount,
        splitMode: c.splitMode,
      })),
  };
}

export const useBillStore = create<BillStoreState>()((set, get) => {
  /** Apply an edit to the open editor and mark it dirty. */
  const edit = (fn: (e: BillEditor) => Partial<BillEditor>) =>
    set((s) => {
      if (!s.editor) return s;
      const next = { ...s.editor, ...fn(s.editor), dirty: true };
      return {
        editor: {
          ...next,
          charges: applyPercentCharges(next.items, next.charges),
        },
      };
    });

  /** Take server-owned share metadata without touching unsaved local edits. */
  const patchMeta = (bill: Bill) =>
    set((s) =>
      s.editor?.billId === bill.id
        ? {
            editor: {
              ...s.editor,
              paymentProfileId: bill.paymentProfileId,
              hasReceipt: bill.hasReceipt,
              share: bill.share,
              flags: bill.flags,
              slips: bill.slips,
              reviewed: Object.fromEntries(
                bill.people.map((p) => [p.id, p.reviewed]),
              ),
              readyToFinalize: bill.readyToFinalize,
              ownerDisplayName: bill.ownerDisplayName,
            },
          }
        : s,
    );

  return {
    bills: [],
    listLoading: false,
    editor: null,
    editorLoading: false,
    remoteChanged: false,
    scanning: false,
    saving: false,
    pendingReceipt: null,

    async loadBills() {
      set({ listLoading: true });
      try {
        set({ bills: await fetchBills() });
      } finally {
        set({ listLoading: false });
      }
    },

    async openBill(id) {
      set({ editorLoading: true, editor: null, remoteChanged: false });
      try {
        set({ editor: fromBill(await fetchBill(id)) });
      } finally {
        set({ editorLoading: false });
      }
    },

    async syncRemote(revision) {
      const editor = get().editor;
      if (!editor?.billId || editor.revision === revision) return;
      if (editor.dirty) {
        set({ remoteChanged: true });
        return;
      }
      const bill = await fetchBill(editor.billId);
      // Re-check: the user may have started editing while the request ran.
      if (get().editor?.dirty) set({ remoteChanged: true });
      else set({ editor: fromBill(bill), remoteChanged: false });
    },

    async reloadRemote() {
      const id = get().editor?.billId;
      if (!id) return;
      set({ editor: fromBill(await fetchBill(id)), remoteChanged: false });
    },

    startNew({ transactionId, title, date }) {
      set({
        pendingReceipt: null,
        editor: {
          billId: null,
          transactionId: transactionId ?? null,
          status: BillStatus.Open,
          transactionAmount: null,
          title: title ?? "",
          date: date ?? dayjs().format("YYYY-MM-DD"),
          goDutch: false,
          people: [ownerPerson()],
          items: [],
          charges: [],
          paid: {},
          finalAmounts: {},
          reviewed: {},
          readyToFinalize: false,
          ownerDisplayName: "",
          paymentProfileId: null,
          hasReceipt: false,
          share: null,
          flags: [],
          slips: [],
          revision: "",
          dirty: false,
        },
      });
    },

    closeEditor() {
      set({ editor: null, remoteChanged: false });
    },

    async scanReceipt(file) {
      set({ scanning: true });
      try {
        const photo = await downscaleImage(file);
        const draft: ReceiptDraft = await parseReceipt(photo);
        set({ pendingReceipt: photo });
        const everyone = (get().editor?.people ?? []).map((p) => p.id);
        edit((e) => ({
          title: e.title || draft.title,
          date: draft.date ?? e.date,
          items: draft.items.map((i) => ({
            key: crypto.randomUUID(),
            name: i.name,
            amount: i.amount,
            quantity: i.quantity,
            claimedBy: everyone.length === 1 ? everyone : [],
          })),
          charges: draft.charges.map((c) => ({
            key: crypto.randomUUID(),
            label: c.label,
            kind: c.kind,
            amount: c.amount,
            splitMode: CHARGE_DEFAULTS[c.kind].splitMode,
          })),
        }));
      } finally {
        set({ scanning: false });
      }
    },

    async removeBill(id) {
      await deleteBill(id);
      set((s) => ({ bills: s.bills.filter((b) => b.id !== id) }));
    },

    setTitle: (title) => edit(() => ({ title })),
    setDate: (date) => edit(() => ({ date })),

    addFriend: (friend) =>
      edit((e) => ({ people: withFriends(e.people, [friend]) })),

    // A group is a snapshot: its Friends are added, ones already here skipped.
    addGroup: (group) =>
      edit((e) => ({
        people: withFriends(
          e.people,
          group.members.map((m) => m.friend),
        ),
      })),

    async relinkPerson(personId, friend) {
      const editor = get().editor;
      if (!editor) return;
      const swap = (people: EditorPerson[]) =>
        people.map((p) =>
          p.id === personId
            ? { ...p, friendId: friend.id, name: friend.name }
            : p,
        );
      // A finalised bill is read-only in the editor, so it saves straight away.
      if (editor.billId && editor.status === BillStatus.Finalized) {
        const bill = await relinkBillPerson(editor.billId, personId, friend.id);
        set((s) =>
          s.editor?.billId === bill.id
            ? { editor: { ...s.editor, people: swap(s.editor.people) } }
            : s,
        );
        return;
      }
      edit((e) => ({ people: swap(e.people) }));
    },

    removePerson: (id) =>
      edit((e) => ({
        people: e.people.filter((p) => p.id !== id),
        items: e.items.map((i) => ({
          ...i,
          claimedBy: i.claimedBy.filter((pid) => pid !== id),
        })),
      })),

    addItem: () =>
      edit((e) => ({
        items: [
          ...e.items,
          {
            key: crypto.randomUUID(),
            name: "",
            amount: 0,
            quantity: 1,
            claimedBy: e.people.length === 1 ? [e.people[0].id] : [],
          },
        ],
      })),

    updateItem: (key, patch) =>
      edit((e) => ({
        items: e.items.map((i) => (i.key === key ? { ...i, ...patch } : i)),
      })),

    removeItem: (key) =>
      edit((e) => ({ items: e.items.filter((i) => i.key !== key) })),

    splitUnits: (key) =>
      edit((e) => ({
        items: e.items.flatMap((i) => (i.key === key ? explodeUnits(i) : [i])),
      })),

    toggleClaim: (key, personId) =>
      edit((e) => ({
        items: e.items.map((i) =>
          i.key !== key
            ? i
            : {
                ...i,
                claimedBy: i.claimedBy.includes(personId)
                  ? i.claimedBy.filter((p) => p !== personId)
                  : [...i.claimedBy, personId],
              },
        ),
      })),

    claimAll: (key) =>
      edit((e) => ({
        items: e.items.map((i) =>
          i.key === key ? { ...i, claimedBy: e.people.map((p) => p.id) } : i,
        ),
      })),

    toggleGoDutch: () => edit((e) => ({ goDutch: !e.goDutch })),

    addCharge: (kind) =>
      edit((e) => ({
        charges: [
          ...e.charges,
          {
            key: crypto.randomUUID(),
            kind,
            amount: 0,
            ...CHARGE_DEFAULTS[kind],
          },
        ],
      })),

    updateCharge: (key, patch) =>
      edit((e) => ({
        charges: e.charges.map((c) =>
          c.key !== key
            ? c
            : {
                ...c,
                ...patch,
                // typing an amount by hand stops the auto %
                ...("amount" in patch ? { percent: undefined } : {}),
              },
        ),
      })),

    removeCharge: (key) =>
      edit((e) => ({ charges: e.charges.filter((c) => c.key !== key) })),

    async save() {
      const editor = get().editor;
      if (!editor) throw new Error("No bill open");
      set({ saving: true });
      try {
        const input = toInput(editor);
        const bill = editor.billId
          ? await saveBill(editor.billId, input)
          : await createBill({
              ...input,
              ...(editor.transactionId
                ? { transactionId: editor.transactionId }
                : {}),
            });
        set({ editor: fromBill(bill) });
        await get().uploadPendingReceipt(bill.id);
        return bill.id;
      } finally {
        set({ saving: false });
      }
    },

    async uploadPendingReceipt(billId) {
      const file = get().pendingReceipt;
      if (!file) return;
      try {
        const bill = await uploadBillReceipt(billId, file);
        set((s) =>
          s.editor?.billId === billId
            ? {
                pendingReceipt: null,
                editor: { ...s.editor, hasReceipt: bill.hasReceipt },
              }
            : { pendingReceipt: null },
        );
      } catch {
        // Non-fatal (e.g. storage not configured): kept, so the next save retries.
      }
    },

    async finalize() {
      const id = await get().save();
      set({ saving: true });
      try {
        set({ editor: fromBill(await finalizeBill(id)) });
      } finally {
        set({ saving: false });
      }
    },

    async reopen() {
      const id = get().editor?.billId;
      if (!id) return;
      set({ saving: true });
      try {
        set({ editor: fromBill(await reopenBill(id)) });
      } finally {
        set({ saving: false });
      }
    },

    async setPaymentProfile(profileId) {
      const id = get().editor?.billId;
      if (!id) return;
      patchMeta(await setBillPaymentProfile(id, profileId));
    },

    async createShare() {
      const id = get().editor?.billId;
      if (!id) return;
      patchMeta(await createBillShare(id));
    },

    async revokeShare() {
      const id = get().editor?.billId;
      if (!id) return;
      patchMeta(await revokeBillShare(id));
    },

    async confirmSlip(slipId) {
      const id = get().editor?.billId;
      if (!id) return;
      const bill = await confirmBillSlip(id, slipId);
      patchMeta(bill);
      // Confirming marks the friend paid; reflect it without touching edits.
      set((s) =>
        s.editor?.billId === id
          ? {
              editor: {
                ...s.editor,
                paid: Object.fromEntries(
                  bill.people.map((p) => [p.id, p.isPaid]),
                ),
              },
            }
          : s,
      );
    },

    async markPersonPaid(personId) {
      const id = get().editor?.billId;
      if (!id) return;
      const bill = await markBillPersonPaid(id, personId);
      patchMeta(bill);
      set((s) =>
        s.editor?.billId === id
          ? {
              editor: {
                ...s.editor,
                paid: Object.fromEntries(
                  bill.people.map((p) => [p.id, p.isPaid]),
                ),
              },
            }
          : s,
      );
    },

    async rejectSlip(slipId) {
      const id = get().editor?.billId;
      if (!id) return;
      patchMeta(await rejectBillSlip(id, slipId));
    },

    async resolveFlag(flagId) {
      const id = get().editor?.billId;
      if (!id) return;
      patchMeta(await resolveBillFlag(id, flagId));
    },
  };
});
